import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Building2, Users, Award, TrendingUp } from 'lucide-react';

const About = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-stone-800 to-stone-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="about-title">{t.about.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* History Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-stone-800 mb-6">{t.about.history_title}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            {t.about.history_text}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {language === 'fr' ? (
              "Aujourd'hui, Barkatis Group incarne la transition réussie entre la rigueur industrielle historique et la valorisation des ressources naturelles tunisiennes. Porté par des valeurs familiales de fiabilité et d'innovation, le Groupe maîtrise désormais une chaîne de valeur complète, allant de la haute technologie industrielle à la distribution agroalimentaire de prestige."
            ) : (
              "Today, Barkatis Group embodies the successful transition between historical industrial rigor and the enhancement of Tunisian natural resources. Driven by family values of reliability and innovation, the Group now masters a complete value chain, ranging from high industrial technology to prestigious agri-food distribution."
            )}
          </p>
        </div>

        {/* Poles Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-stone-800 mb-8 text-center">{t.about.poles_title}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Pole Industry */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-orange-500" data-testid="pole-industry">
              <Building2 className="text-orange-500 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-stone-800 mb-3">{t.about.pole_industry}</h3>
              <p className="text-gray-600 mb-4">
                {language === 'fr' ? (
                  "Le socle historique de notre savoir-faire, garantissant la performance des infrastructures nationales."
                ) : (
                  "The historic foundation of our expertise, ensuring the performance of national infrastructure."
                )}
              </p>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>• <strong>Tout pour la Céramique</strong> (Depuis 1993)</li>
                <li>• <strong>Comptoir des Moteurs</strong> (Depuis 2008)</li>
              </ul>
            </div>

            {/* Pole Art */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-purple-500" data-testid="pole-art">
              <Award className="text-purple-500 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-stone-800 mb-3">{t.about.pole_art}</h3>
              <p className="text-gray-600 mb-4">
                {language === 'fr' ? (
                  "L'alliance de la matière et de l'esthétique pour l'architecture moderne."
                ) : (
                  "The alliance of material and aesthetics for modern architecture."
                )}
              </p>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>• <strong>Sodever</strong> (Depuis 2015) - Mosaïques en verre</li>
              </ul>
            </div>

            {/* Pole Agro */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-green-500" data-testid="pole-agro">
              <TrendingUp className="text-green-500 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-stone-800 mb-3">{t.about.pole_agro}</h3>
              <p className="text-gray-600 mb-4">
                {language === 'fr' ? (
                  "Une intégration verticale unique, de l'arbre jusqu'au consommateur final."
                ) : (
                  "A unique vertical integration, from tree to final consumer."
                )}
              </p>
              <ul className="text-gray-700 space-y-2 text-sm">
                <li>• <strong>Barkatis Group Agricole</strong> (Depuis 2016)</li>
                <li>• <strong>Disfood</strong> (Depuis 2022)</li>
                <li>• <strong>Hron Huile</strong> (Depuis 2025)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-amber-50 rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-stone-800 mb-6">{t.about.why_title}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-xl font-bold text-amber-700 mb-2">
                {language === 'fr' ? 'Maîtrise Totale' : 'Total Control'}
              </h3>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "De la culture à la distribution et au conditionnement, nous contrôlons chaque étape pour garantir une qualité irréprochable."
                ) : (
                  "From cultivation to distribution and packaging, we control every step to ensure impeccable quality."
                )}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-amber-700 mb-2">
                {language === 'fr' ? 'Solidité Institutionnelle' : 'Institutional Strength'}
              </h3>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Un groupe familial établi depuis 1993, offrant la stabilité financière et opérationnelle indispensable."
                ) : (
                  "A family-owned group established since 1993, offering essential financial and operational stability."
                )}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-amber-700 mb-2">
                {language === 'fr' ? 'Exigence de Modernité' : 'Commitment to Modernity'}
              </h3>
              <p className="text-gray-700">
                {language === 'fr' ? (
                  "Nous appliquons la précision du secteur industriel aux produits de la terre, utilisant des technologies de pointe."
                ) : (
                  "We apply industrial sector precision to agricultural products, using cutting-edge technologies."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Leader Quote */}
        <div className="bg-stone-800 text-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold mb-6">{t.about.leader_title}</h2>
          <blockquote className="text-xl italic leading-relaxed">
            {t.about.leader_quote}
          </blockquote>
          <p className="mt-4 text-amber-400 font-semibold">- M. Bechir Barkatis</p>
        </div>
      </div>
    </div>
  );
};

export default About;